import { ROUTES } from "@/lib/routes";
import { deepMerge } from "@/lib/deepMerge";

export function articlePath(slug) {
  return `${ROUTES.articles}/${slug}`;
}

export function normalizeArticle(raw) {
  if (!raw || typeof raw !== "object") return null;
  const slug = String(raw.slug || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) return null;
  return {
    slug,
    title: String(raw.title || "").trim(),
    excerpt: String(raw.excerpt || "").trim(),
    coverUrl: String(raw.coverUrl || "").trim(),
    publishedAt: String(raw.publishedAt || "").trim(),
    author: String(raw.author || "").trim(),
    status: raw.status === "draft" ? "draft" : "published",
    body: typeof raw.body === "string" ? raw.body : "",
    seoTitle: String(raw.seoTitle || "").trim(),
    seoDescription: String(raw.seoDescription || "").trim(),
  };
}

/**
 * DB overrides replace arrays wholesale in deepMerge. Merge by slug so
 * admin-edited articles win, while new code defaults still appear.
 */
export function mergeArticlesBySlug(defaults, overrides) {
  const def = Array.isArray(defaults) ? defaults : [];
  if (!Array.isArray(overrides)) return def;

  const seen = new Set();
  const out = [];
  for (const raw of overrides) {
    const n = normalizeArticle(raw);
    if (!n || seen.has(n.slug)) continue;
    seen.add(n.slug);
    out.push(raw);
  }
  for (const raw of def) {
    const n = normalizeArticle(raw);
    if (!n || seen.has(n.slug)) continue;
    seen.add(n.slug);
    out.push(raw);
  }
  return out;
}

export function mergeLangTranslations(base, override) {
  const ov = override && typeof override === "object" && !Array.isArray(override) ? override : {};
  const merged = deepMerge(base || {}, ov);
  return {
    ...merged,
    articles: mergeArticlesBySlug(base?.articles, ov.articles),
  };
}

export function getArticles(t) {
  const list = Array.isArray(t?.articles) ? t.articles : [];
  return list.map(normalizeArticle).filter(Boolean);
}

export function getPublishedArticles(t) {
  return getArticles(t)
    .filter((a) => a.status === "published" && a.title)
    .sort((a, b) => String(b.publishedAt).localeCompare(String(a.publishedAt)));
}

export function getArticleBySlug(t, slug) {
  const key = String(slug || "").trim().toLowerCase();
  return getPublishedArticles(t).find((a) => a.slug === key) || null;
}

export function formatArticleDate(iso, lang = "ro") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}
